import { useMemo, useState } from 'react';
import { LiveScreen, QueryState, formatDateTime, type Column } from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { StatCards, applyStat } from '@/components/StatCards';
import { useRows } from '@/hooks/useResource';
import { FarmerPhoto, usePhotoUrls } from '@/components/FarmerPhoto';
import { RecordDrawer } from '@/components/RecordDrawer';

interface ReferenceRow {
  referrer_key: string;
  id?: string;
  referred_by_type: string;
  referred_by_name: string | null;
  referred_by_phone: string | null;
  referred_by_farmer_id: string | null;
  farmers_referred: number;
  farmers: string;
  last_referral_at: string;
  /** The referring farmer's own photo, or the latest one recorded with a name-based referrer. */
  referrer_photo_document_id: string | null;
}

const TYPE_LABEL: Record<string, string> = {
  farmer: 'Farmer', lead_farmer: 'Lead farmer', field_staff: 'Field staff', stakeholder: 'Stakeholder',
  community_leader: 'Community leader', other: 'Other',
};

/**
 * Who brought whom into the programme (owner, 2026-09-21: "who gave reference to the farmer and a
 * reference list"). One row per referrer with the members they referred, built from the farmer
 * register - so it covers exactly the members the caller may see.
 */
export default function ReferencesScreen() {
  const [stat, setStat] = useState<string | null>(null);
  // A referrer opens as a card to read (owner, 2026-09-27: "on click on datagrid ... shall show
  // detailed view"). It is a roll-up of the member register, so it is changed on the member's form.
  const [open, setOpen] = useState<ReferenceRow | null>(null);
  const refs = useRows<ReferenceRow>('farmer_references', { orderBy: { column: 'farmers_referred', ascending: false } });
  const rows = useMemo(() => (refs.data ?? []).map((row) => ({ ...row, id: row.referrer_key })), [refs.data]);

  const STATS = useMemo(() => [
    { key: 'all', label: 'Referrers', match: () => true, tone: 'neutral' as const },
    ...Object.entries(TYPE_LABEL).map(([key, label]) => ({
      key, label, tone: 'info' as const, match: (row: ReferenceRow) => row.referred_by_type === key,
    })),
  ], []);
  const visible = useMemo(() => applyStat(rows, STATS, stat), [rows, STATS, stat]);
  const photos = usePhotoUrls(rows.map((row) => row.referrer_photo_document_id));

  const columns: Array<Column<ReferenceRow>> = [
    { key: 'referred_by_name', label: 'Referred by', render: (row) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <FarmerPhoto name={row.referred_by_name} size={30}
                     url={row.referrer_photo_document_id ? photos.data?.[row.referrer_photo_document_id] : null} />
        <strong>{row.referred_by_name ?? '--'}</strong>
      </span>
    ) },
    { key: 'referred_by_type', label: 'Type', render: (row) => TYPE_LABEL[row.referred_by_type] ?? row.referred_by_type },
    { key: 'referred_by_phone', label: 'Phone', render: (row) => row.referred_by_phone ?? '--' },
    { key: 'farmers_referred', label: 'Members referred', align: 'right' },
    { key: 'farmers', label: 'Members', render: (row) => <span className="muted">{row.farmers}</span> },
    { key: 'last_referral_at', label: 'Latest', render: (row) => formatDateTime(row.last_referral_at) },
  ];

  return (
    <LiveScreen subtitle="Who referred which members into the programme." title="References">
      <StatCards active={stat} onSelect={setStat} rows={rows} stats={STATS} />
      <QueryState empty="No member has a referral recorded yet. Add one on the member's form, under Referred by." query={refs}>
        <DataGrid columns={columns} exportName="references" onRowClick={setOpen} rows={visible} screenKey="farmer.register"
                  selectedId={open?.id} />
      </QueryState>
      <RecordDrawer footer={<button onClick={() => setOpen(null)} type="button">Close</button>} onClose={() => setOpen(null)}
                    open={open !== null} record={null} subtitle={open ? TYPE_LABEL[open.referred_by_type] ?? open.referred_by_type : undefined}
                    title={open?.referred_by_name ?? 'Referrer'}>
        {open && (
          <>
            <FarmerPhoto name={open.referred_by_name} size={96}
                         url={open.referrer_photo_document_id ? photos.data?.[open.referrer_photo_document_id] : null} />
            <dl className="detail-grid spec-view">
              <div><dt>Referred by</dt><dd>{open.referred_by_name ?? '--'}</dd></div>
              <div><dt>Type</dt><dd>{TYPE_LABEL[open.referred_by_type] ?? open.referred_by_type}</dd></div>
              <div><dt>Phone</dt><dd>{open.referred_by_phone ?? '--'}</dd></div>
              <div><dt>Latest referral</dt><dd>{formatDateTime(open.last_referral_at)}</dd></div>
              <div className="spec-view-wide"><dt>Members referred ({open.farmers_referred})</dt><dd>{open.farmers}</dd></div>
            </dl>
            <p className="muted">A referral is recorded on the member's form, under Referred by: change it there.</p>
          </>
        )}
      </RecordDrawer>
    </LiveScreen>
  );
}
